import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { hasValidCoordinates } from 'shared/utils';
import { useAuthState } from 'state/useAuthState/useAuthState';
import { fetchPermissions } from './usePermissions.queries';
import { isPermissionsQueryEnabled } from './usePermissions.utils';
import { PermissionsData, PermissionsParams } from './usePermissions.types';

// 4 decimal places (~11m) is sufficient for state-level resolution
const COORDINATE_PRECISION = 4;

const normalizeParams = ({
  lat,
  lng,
  geoEventId,
}: PermissionsParams): PermissionsParams =>
  ({
    geoEventId,
    ...(lat != null && { lat: parseFloat(lat.toFixed(COORDINATE_PRECISION)) }),
    ...(lng != null && { lng: parseFloat(lng.toFixed(COORDINATE_PRECISION)) }),
  }) as PermissionsParams;

export const usePermissions = (
  params: PermissionsParams,
): UseQueryResult<PermissionsData> => {
  const { user } = useAuthState();

  const hasRequiredParams =
    Boolean(params.geoEventId) || hasValidCoordinates(params);

  const normalizedParams = normalizeParams(params);

  return useQuery({
    queryKey: ['permissions', normalizedParams],
    queryFn: () => fetchPermissions(normalizedParams),
    enabled:
      isPermissionsQueryEnabled({
        geoEventType: params.geoEventType,
        user,
      }) && hasRequiredParams,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};
